/**
 * Pipeline-family decisions on the Verification tab (inc5 step F — the
 * routing split). The AESEV-class true corrections live HERE now, in
 * clinical-data-manager language, replacing the retired EDC Build
 * NeedsReviewCard so nothing is actionable in two places.
 *
 * Rows:
 *  - offered corrections → customer offer lines (correctionCopy) with
 *    Approve (confirm + preview, server re-plans from live data and can
 *    refuse) and Decline (reason required);
 *  - guidance rows (ambiguous mapping keys, DD staleness) → plain guidance
 *    plus "Take me there";
 *  - applied auto-clears → one disclosure line, never a decision.
 * Failed corrections render NOWHERE here — builder alerting only.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpsCallable } from '../../lib/callable';
import { ArrowRight, ClipboardCheck, Loader2, ShieldCheck, XCircle } from 'lucide-react';
import { functions } from '../../firebase';
import { CLOUD_FUNCTIONS, CALLABLE_TIMEOUTS } from '../../constants/firebase';
import { useToast } from '../../contexts/ToastContext';
import ConfirmDialog from '../ConfirmDialog';
import {
  ambiguityLine,
  approvedCorrectionToast,
  ddStaleLine,
  mappingOfferAction,
  mappingOfferLine,
  proseFindingLine,
} from '../../features/corrections/correctionCopy';
import type { GuidanceRow } from '../../features/corrections/correctionRows';
import type { PipelineReviewData } from '../../features/corrections/usePipelineReview';
import type { ReviewRow } from '../../features/corrections/correctionRows';

const OTHER_REASON = 'Other';
const DECLINE_REASONS = [
  'Intentional design choice',
  'Not applicable to this study',
  'Waiting on an upstream document change',
  OTHER_REASON,
] as const;

const OFFER_LINES_SHOWN = 5;

interface PipelineReviewSectionProps {
  projectId: string;
  data: PipelineReviewData;
}

/** Customer lines for one offer row, from its persisted preview. */
function offerLines(row: ReviewRow, cap = OFFER_LINES_SHOWN): string[] {
  const before = row.preview?.before?.entries ?? {};
  const after = row.preview?.after?.entries ?? {};
  const keys = Object.keys(before);
  const lines = keys.slice(0, cap).map((k) => mappingOfferLine(k, before[k] ?? '(none)', after[k] ?? '(none)'));
  const total = row.preview?.before?.total ?? keys.length;
  if (total > lines.length) lines.push(`…and ${total - lines.length} more field${total - lines.length === 1 ? '' : 's'}.`);
  if (lines.length === 0) lines.push(row.title); // legacy record without preview
  return lines;
}

export default function PipelineReviewSection({ projectId, data }: PipelineReviewSectionProps) {
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [busyId, setBusyId] = useState<string | null>(null);
  const [approveTarget, setApproveTarget] = useState<ReviewRow | null>(null);
  const [declineTarget, setDeclineTarget] = useState<ReviewRow | null>(null);
  const [declineChoice, setDeclineChoice] = useState('');
  const [declineText, setDeclineText] = useState('');
  const [dismissTarget, setDismissTarget] = useState<GuidanceRow | null>(null);
  const [dismissChoice, setDismissChoice] = useState('');
  const [dismissText, setDismissText] = useState('');

  const { offers, guidance, autoClearLines, s3Note } = data;
  if (offers.length === 0 && guidance.length === 0 && autoClearLines === null && s3Note === null) return null;

  async function doApprove(row: ReviewRow) {
    setBusyId(row.correctionId);
    try {
      const res: any = (
        await httpsCallable(functions, CLOUD_FUNCTIONS.APPLY_CORRECTION_V2, {
          timeout: CALLABLE_TIMEOUTS.MEDIUM,
        })({ projectId, family: row.family, correctionId: row.correctionId, action: 'apply' })
      ).data;
      if (res && res.applied === false) {
        showToast(res.refusedReason || 'The correction could not be applied.', 'error');
        return;
      }
      showToast(approvedCorrectionToast(res?.summary), 'success');
      // Refresh the record so the row clears without a manual re-run.
      httpsCallable(functions, CLOUD_FUNCTIONS.RUN_PIPELINE_VERIFICATION_V2, {
        timeout: CALLABLE_TIMEOUTS.SHORT,
      })({ projectId }).catch(() => {});
    } catch (e: any) {
      showToast(`Could not apply the correction: ${e?.message || 'unknown error'}`, 'error');
    } finally {
      setBusyId(null);
      setApproveTarget(null);
    }
  }

  function effectiveDeclineReason(): string {
    return declineChoice === OTHER_REASON ? declineText.trim() : declineChoice.trim();
  }

  async function doDecline(row: ReviewRow) {
    const reason = effectiveDeclineReason();
    if (!reason) {
      showToast('A reason is required to decline a correction.', 'warning');
      return;
    }
    setBusyId(row.correctionId);
    try {
      await httpsCallable(functions, CLOUD_FUNCTIONS.APPLY_CORRECTION_V2, {
        timeout: CALLABLE_TIMEOUTS.MEDIUM,
      })({ projectId, family: row.family, correctionId: row.correctionId, action: 'decline', reason });
      showToast('Correction declined.', 'success');
    } catch (e: any) {
      showToast(`Could not record the decision: ${e?.message || 'unknown error'}`, 'error');
    } finally {
      setBusyId(null);
      setDeclineTarget(null);
      setDeclineChoice('');
      setDeclineText('');
    }
  }

  return (
    <div className="card border-l-4 border-l-amber-400 mb-6">
      <div className="flex items-start gap-3 px-5 py-4">
        <div className="h-9 w-9 rounded-lg flex items-center justify-center bg-amber-100 shrink-0">
          <ClipboardCheck className="h-5 w-5 text-amber-600" />
        </div>
        <div className="min-w-0">
          <span className="font-semibold text-gray-900 text-sm">
            Your data dictionary and SDTM mapping
          </span>
          <p className="text-xs text-gray-500 mt-0.5">
            Differences between documents that need your decision. Approving corrects the
            document; the previous values are recorded and recoverable.
          </p>
        </div>
      </div>

      <ul className="px-5 pb-4 space-y-2">
        {offers.map((row) => (
          <li
            key={row.correctionId}
            className={
              'rounded-lg border px-3 py-2.5 ' +
              (row.declined ? 'border-gray-200 bg-gray-50 opacity-70' : 'border-amber-200 bg-amber-50/40')
            }
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <ul className="space-y-0.5">
                  {offerLines(row).map((line, i) => (
                    <li key={i} className="text-sm text-gray-900">{line}</li>
                  ))}
                </ul>
                {!row.declined && (
                  <p className="text-xs text-gray-500 mt-1">{mappingOfferAction()}</p>
                )}
                {row.declined && (
                  <span className="inline-flex items-center gap-1 text-[11px] text-gray-500 mt-1">
                    <XCircle className="h-3 w-3" /> Declined{row.declineReason ? ` — ${row.declineReason}` : ''}
                  </span>
                )}
              </div>
              {!row.declined && (
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => setApproveTarget(row)}
                    disabled={busyId !== null}
                    className="px-2.5 py-1.5 text-xs font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
                  >
                    {busyId === row.correctionId ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Approve'}
                  </button>
                  <button
                    onClick={() => setDeclineTarget(row)}
                    disabled={busyId !== null}
                    className="px-2.5 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                  >
                    Decline
                  </button>
                </div>
              )}
            </div>
          </li>
        ))}

        {guidance.map((g) => (
          <li key={`${g.checkId ?? g.kind}:${g.subject}`} className="rounded-lg border border-amber-200 bg-amber-50/40 px-3 py-2.5">
            <div className="flex items-start justify-between gap-3">
              <span className="text-sm text-gray-900">
                {g.kind === 'ambiguous'
                  ? ambiguityLine(g.subject, g.data?.variants ?? '')
                  : g.kind === 'dd-stale'
                    ? ddStaleLine()
                    : proseFindingLine(g) ?? g.subject}
              </span>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() => navigate(`/project/${projectId}/data-management`)}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Take me there <ArrowRight className="h-3 w-3" />
                </button>
                {g.checkId && g.fingerprint && (
                  <button
                    onClick={() => setDismissTarget(g)}
                    className="px-2.5 py-1.5 text-xs font-medium text-gray-500 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
                  >
                    Dismiss
                  </button>
                )}
              </div>
            </div>
          </li>
        ))}

        {s3Note !== null && (
          <li className="rounded-lg border border-amber-200 bg-amber-50/40 px-3 py-2.5 text-sm text-amber-900">
            {s3Note}
          </li>
        )}

        {autoClearLines !== null && (
          <li className="rounded-lg border border-emerald-200 bg-emerald-50/40 px-3 py-2.5">
            <span className="inline-flex items-start gap-2 text-sm text-emerald-900">
              <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0 text-emerald-600" />
              <span className="space-y-1">
                {autoClearLines.map((line) => (
                  <span key={line} className="block">
                    {line}
                  </span>
                ))}
              </span>
            </span>
          </li>
        )}
      </ul>

      {approveTarget && (
        <ConfirmDialog
          title="Apply this correction?"
          destructive={approveTarget.destructive}
          confirmLabel="Apply correction"
          busy={busyId === approveTarget.correctionId}
          onConfirm={() => void doApprove(approveTarget)}
          onCancel={() => setApproveTarget(null)}
        >
          <ul className="max-h-40 overflow-y-auto space-y-0.5 bg-gray-50 rounded-lg p-2">
            {offerLines(approveTarget).map((line) => (
              <li key={line} className="text-xs text-gray-600">{line}</li>
            ))}
          </ul>
          <p>
            The dictionary will be updated to match the mapping. The change is re-checked
            against the current documents before anything is written, and the previous
            values are recorded and recoverable.
          </p>
        </ConfirmDialog>
      )}

      {dismissTarget && (
        <ConfirmDialog
          title="Dismiss this finding?"
          confirmLabel="Dismiss"
          onConfirm={() => {
            const reason = dismissChoice === OTHER_REASON ? dismissText.trim() : dismissChoice.trim();
            if (!reason) {
              showToast('A reason is required to dismiss a finding.', 'warning');
              return;
            }
            void (async () => {
              try {
                await httpsCallable(functions, CLOUD_FUNCTIONS.DISMISS_VALIDATION_FINDING_V2, {
                  timeout: CALLABLE_TIMEOUTS.MEDIUM,
                })({
                  projectId,
                  checkId: dismissTarget.checkId,
                  subject: dismissTarget.subject,
                  fingerprint: dismissTarget.fingerprint,
                  reason,
                });
                showToast('Finding dismissed.', 'success');
              } catch (e: any) {
                showToast(`Could not dismiss: ${e?.message || 'unknown error'}`, 'error');
              } finally {
                setDismissTarget(null);
                setDismissChoice('');
                setDismissText('');
              }
            })();
          }}
          onCancel={() => {
            setDismissTarget(null);
            setDismissChoice('');
            setDismissText('');
          }}
        >
          <p>The finding stays recorded with your reason and returns only if its content changes.</p>
          <div className="space-y-2 mt-2">
            {DECLINE_REASONS.map((r) => (
              <label key={r} className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="radio"
                  name="dismiss-reason"
                  checked={dismissChoice === r}
                  onChange={() => setDismissChoice(r)}
                />
                {r}
              </label>
            ))}
            {dismissChoice === OTHER_REASON && (
              <input
                type="text"
                value={dismissText}
                onChange={(e) => setDismissText(e.target.value)}
                placeholder="Reason"
                className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
              />
            )}
          </div>
        </ConfirmDialog>
      )}

      {declineTarget && (
        <ConfirmDialog
          title="Decline this correction?"
          confirmLabel="Decline"
          busy={busyId === declineTarget.correctionId}
          onConfirm={() => void doDecline(declineTarget)}
          onCancel={() => {
            setDeclineTarget(null);
            setDeclineChoice('');
            setDeclineText('');
          }}
        >
          <p>The difference stays recorded with your reason, and the documents are left as they are.</p>
          <div className="space-y-2 mt-2">
            {DECLINE_REASONS.map((r) => (
              <label key={r} className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="radio"
                  name="decline-reason"
                  checked={declineChoice === r}
                  onChange={() => setDeclineChoice(r)}
                />
                {r}
              </label>
            ))}
            {declineChoice === OTHER_REASON && (
              <input
                type="text"
                value={declineText}
                onChange={(e) => setDeclineText(e.target.value)}
                placeholder="Reason"
                className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
              />
            )}
          </div>
        </ConfirmDialog>
      )}
    </div>
  );
}
